/**
 * Assist beside another Mauvely app: `/panel?app=<key>`, loaded by Compose's
 * and Mail's side pane and by Assist desktop's window beside Snap or Relay
 * (docs/desktop-bridge.md). One opaque column for a 360–480px pane: the app's
 * name and "Open in Assist", the chat, and the composer with the model and
 * effort as one chip.
 *
 * The host publishes the desktop app's `assistDesktop` bridge with its own
 * tools, so the turn store runs their calls as it does in the desktop app.
 * Each turn sent from here also asks the app what it has open (`hostApp`).
 *
 * The first message makes the chat, an ordinary one, and New chat starts the
 * next. Nothing is kept in the browser: the panel opens on a new chat each time
 * it loads. Links inside it open the rest of Assist in its own window.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_EFFORT, HOST_APP } from '@wire';
import {
  ApiError,
  createChat,
  getChat,
  patchChat,
  setLeaf,
  type Attachment,
  type ChatPatch,
  type ChatSummary,
  type ContextUse,
  type Effort,
  type Message
} from '../api';
import { setFront } from '../hub';
import { Composer } from '../components/composer/Composer';
import { MessageList } from '../components/MessageList';
import { answeredText, answerItems, pendingAsk } from '../components/pendingAsk';
import { BareShell } from '../components/Shell';
import type { Decision } from '../components/ToolCalls';
import { IconTap, Ph } from '../components/ui';
import { toSummary } from '../hooks/chatStash';
import { useSelectAll } from '../hooks/useSelectAll';
import { useStickToBottom } from '../hooks/useStickToBottom';
import {
  forgetTurn,
  queueTurn,
  setResearch,
  startTurn,
  stopTurn,
  takeOn,
  takeQueued,
  useLiveTurn,
  useResearch,
  withTurn,
  type QueuedTurn
} from '../hooks/useTurns';
import { OutwardLinks, useRouter } from '../router';
import { useApp } from '../state';

const failed = (err: unknown): string =>
  err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.';

export function PanelPage() {
  const { me, catalogue, upsertChat, touchChat, patchMe, refreshMe } = useApp();
  const { query } = useRouter();
  const key = query.get('app') ?? '';
  const hostApp = HOST_APP.test(key) ? key : null;
  const host = hostApp ? { hostApp } : {};
  const app = useMemo(() => ({ upsertChat, touchChat, patchMe, refreshMe }), [upsertChat, touchChat, patchMe, refreshMe]);

  const [chat, setChat] = useState<ChatSummary | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [context, setContext] = useState<ContextUse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The part of a reply the next message answers, picked with Quote. */
  const [quote, setQuote] = useState<string | null>(null);
  const live = useLiveTurn(chat?.id ?? '');
  // Research is the chat's once there is one; before, it waits for the first message.
  const kept = useResearch(chat?.id);
  const [early, setEarly] = useState(false);
  const research = chat ? kept : early;
  const { scrollRef, contentRef, content, stick } = useStickToBottom();
  useSelectAll(content);

  // The account's default, unless the catalogue no longer offers it here.
  const [picked, setPicked] = useState<string | null>(null);
  const model = useMemo(() => {
    if (picked) return picked;
    const preferred = me?.settings.defaultModel ?? '';
    if (!catalogue) return preferred;
    const offered = catalogue.models.some((m) => m.id === preferred && m.group !== 'device');
    return offered ? preferred : catalogue.defaultModel;
  }, [picked, me?.settings.defaultModel, catalogue]);
  const [pickedEffort, setPickedEffort] = useState<Effort | null>(null);
  const effort = pickedEffort ?? me?.settings.defaultEffort ?? DEFAULT_EFFORT;

  // Once there is a chat a pick is the chat's too, one PATCH after another,
  // so the last pick is the one kept.
  const saving = useRef<Promise<void>>(Promise.resolve());
  const save = (patch: ChatPatch) => {
    if (!chat) return;
    const id = chat.id;
    saving.current = saving.current.then(() => patchChat(id, patch).then(() => undefined, () => undefined));
  };

  // Fold a settled turn in, so the next one can take its place in the store.
  // Then what was queued while it streamed goes, unless the reply waits.
  useEffect(() => {
    if (!live || live.phase === 'streaming') return;
    setMessages((list) => withTurn(list, live, true));
    if (live.context) setContext(live.context);
    const body = live.final?.body;
    const waits = body?.kind === 'text' && (!!body.pending?.length || !!body.stepLimit);
    if (live.phase === 'done' && !waits && chat) {
      const q = takeQueued(chat.id);
      if (q) sendQueued(chat, q);
    }
  }, [live]);

  // A reply that ended out of sight: the chat as it is now, and what was
  // queued behind it goes.
  const endedSeq = live?.failure?.code === 'ended' ? live.seq : null;
  useEffect(() => {
    if (endedSeq === null || !chat) return;
    getChat(chat.id).then(
      (next) => {
        forgetTurn(chat.id);
        setMessages(next.messages);
        const q = takeQueued(chat.id);
        if (q) sendQueued(chat, q);
      },
      (err: unknown) => setError(failed(err))
    );
  }, [endedSeq]);

  // The hub hears which chat is in front, for as long as it is.
  const chatId = chat?.id ?? null;
  useEffect(() => {
    setFront(chatId);
    return () => setFront(null);
  }, [chatId]);

  const thread = useMemo(() => withTurn(messages, live), [messages, live]);
  const question = useMemo(() => pendingAsk(thread), [thread]);
  const streaming = live?.phase === 'streaming';
  const inAssist = chat ? `${window.location.origin}/chat/${chat.id}` : null;

  const send = async (text: string, attachments: Attachment[]) => {
    // Sent while a reply streams, it waits for the reply.
    if (streaming && chat) {
      queueTurn(chat.id, { text, attachments, ...(quote ? { quote } : {}) });
      setQuote(null);
      return;
    }
    setError(null);
    let c = chat;
    if (!c) {
      try {
        c = toSummary(await createChat({ projectId: null, model, effort }));
      } catch (err) {
        if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
        setError(failed(err));
        // Thrown, the composer keeps the draft.
        throw err;
      }
      upsertChat(c);
      setChat(c);
      if (early) setResearch(c.id, true);
    }
    stick();
    const answers = question?.messageId;
    startTurn({
      chat: c,
      kind: 'turn',
      input: { text, attachments, ...(answers ? { answers } : {}), ...(quote ? { quote } : {}), ...host },
      app
    });
    setQuote(null);
  };

  const sendQueued = (c: ChatSummary, q: QueuedTurn) => {
    setError(null);
    stick();
    const answers = question?.messageId;
    startTurn({
      chat: c,
      kind: 'turn',
      input: { text: q.text, attachments: q.attachments, ...(answers ? { answers } : {}), ...(q.quote ? { quote: q.quote } : {}), ...host },
      app
    });
  };

  /** Stop the reply where it is, and send what was queued. */
  const sendNow = () => {
    if (!chat) return;
    const stopped = stopTurn(chat.id, true);
    if (stopped) setMessages((list) => withTurn(list, stopped, true));
    const q = takeQueued(chat.id);
    if (q) sendQueued(chat, q);
  };

  const answer = (picks: string[]) => {
    if (!chat || !question) return;
    const answered = answerItems(question.ask, picks);
    stick();
    startTurn({
      chat,
      kind: 'turn',
      input: { text: answeredText(answered), attachments: [], answers: question.messageId, picks, answered, ...host },
      app
    });
  };

  const edit = (messageId: string, text: string) => {
    if (!chat) return;
    const original = thread.find((m) => m.id === messageId);
    const attachments = original?.body.kind === 'text' ? (original.body.attachments ?? []) : [];
    setError(null);
    stick();
    // The new version keeps the old one's quote, as the Worker does.
    const kept = original?.body.kind === 'text' ? original.body.quote : undefined;
    startTurn({ chat, kind: 'turn', input: { text, attachments, edits: messageId, ...(kept ? { quote: kept } : {}), ...host }, app });
  };

  const regenerate = (messageId: string) => {
    if (!chat) return;
    setError(null);
    stick();
    startTurn({ chat, kind: 'retry', replacing: messageId, app });
  };

  const decide = (message: Message, decision: Decision) => {
    if (!chat || message.body.kind !== 'text') return;
    const approvals = (message.body.pending ?? []).filter((c) => c.needs === 'approval').map((c) => ({ callId: c.id, decision }));
    const resume = message.body.stepLimit ? (decision === 'denied' ? { stop: true } : {}) : { approvals };
    setError(null);
    stick();
    startTurn({ chat, kind: 'continue', waiting: message, resume, app });
  };

  const switchVersion = async (messageId: string) => {
    if (!chat) return;
    setError(null);
    try {
      const next = await setLeaf(chat.id, { messageId });
      forgetTurn(chat.id);
      setMessages(next.messages);
    } catch (err) {
      setError(failed(err));
    }
  };

  /** Refused, the message goes again; failed part-way, the reply is made
   *  again; a refused take-on of a waiting reply is taken on again. */
  const retryFailed = () => {
    if (!chat || !live?.failure) return;
    const { refused } = live.failure;
    setError(null);
    stick();
    if (live.kind === 'retry' && live.replacing) regenerate(refused ? live.replacing : (live.messageId ?? live.replacing));
    else if (live.messageId && !refused) regenerate(live.messageId);
    else if (live.input) startTurn({ chat, kind: 'turn', input: live.input, app });
    else if (live.waiting) {
      forgetTurn(chat.id);
      takeOn({ chat, message: live.waiting, app, again: true });
    }
  };

  const failure = live?.phase === 'failed' && live.failure?.code !== 'ended' ? live.failure : null;

  const startOver = () => {
    setChat(null);
    setMessages([]);
    setContext(null);
    setError(null);
    setQuote(null);
  };

  return (
    <BareShell>
      <OutwardLinks>
        <div
          className="mv-app"
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--app-panel)',
            color: 'var(--text-body)',
            fontFamily: 'var(--font-body)',
            overflow: 'hidden'
          }}
        >
          <header
            style={{
              flex: 'none',
              height: 44,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 8px 0 14px'
            }}
          >
            <Ph name="sparkle" size={15} color="var(--primary)" />
            <span
              style={{
                fontWeight: 600,
                fontSize: 13.5,
                color: 'var(--text-strong)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0
              }}
            >
              {hostApp ? hostApp[0]!.toUpperCase() + hostApp.slice(1) : 'Assist'}
            </span>
            <span style={{ flex: 1 }} />
            {inAssist ? (
              <a
                href={inAssist}
                target="_blank"
                rel="noopener noreferrer"
                className="hov-tint"
                style={{
                  flex: 'none',
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '0 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  color: 'var(--primary-text)'
                }}
              >
                Open in Assist
                <Ph name="arrow-square-out" size={13} />
              </a>
            ) : null}
            {chat ? <IconTap icon="note-pencil" label="New chat" disabled={streaming} onClick={startOver} /> : null}
          </header>

          <div ref={scrollRef} className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <div ref={contentRef} style={{ padding: '12px 14px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {chat ? (
                <MessageList
                  chatId={chat.id}
                  messages={thread}
                  live={streaming ? live : null}
                  busy={streaming}
                  onRetry={regenerate}
                  onEdit={edit}
                  onDecide={decide}
                  onSwitch={(messageId) => void switchVersion(messageId)}
                  onChange={(message) => setMessages((list) => list.map((m) => (m.id === message.id ? message : m)))}
                  onOpenArtifact={() => inAssist && window.open(inAssist, '_blank', 'noopener')}
                  onQuote={setQuote}
                />
              ) : null}
              {failure || error ? (
                <div
                  role="alert"
                  style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-muted)' }}
                >
                  <Ph name="warning-circle" size={15} color="var(--danger-500)" />
                  <span>{failure?.message ?? error}</span>
                  {failure ? (
                    <button type="button" className="bare" onClick={retryFailed} style={{ fontSize: 13.5, color: 'var(--text-link)' }}>
                      Retry
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ flex: 'none', padding: '8px 12px 12px' }}>
            <Composer
              variant={chat ? 'chat' : 'home'}
              {...(chat ? { chatId: chat.id } : {})}
              model={model}
              onModelChange={(id) => {
                setPicked(id);
                save({ model: id });
              }}
              effort={effort}
              onEffortChange={(e) => {
                setPickedEffort(e);
                save({ effort: e });
              }}
              context={context}
              suggestion={live?.phase === 'done' ? live.suggestion : null}
              question={question}
              onAnswer={answer}
              busy={streaming}
              onStop={() => chat && stopTurn(chat.id)}
              onSendNow={sendNow}
              onSend={send}
              quote={quote}
              onQuoteClear={() => setQuote(null)}
              onQuoteRestore={setQuote}
              research={research}
              onResearch={(on) => (chat ? setResearch(chat.id, on) : setEarly(on))}
              autoFocus
            />
          </div>
        </div>
      </OutwardLinks>
    </BareShell>
  );
}
